import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Avatar, Badge, Dropdown } from "flowbite-react";
import { permissionChecker, removeToken } from "../utils/permissions";
import { useApplication } from "../contexts/ApplicationContext";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import axios from "../config/axiosInstance";
import { useQuery } from "react-query";
import { PAYMENTS } from "../config/apiConfig";
import {
  IoIosArrowBack,
  IoIosArrowDown,
  IoIosArrowForward,
  IoMdRefresh,
} from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import ModalAddCredit from "./modals/ModalAddCredit";
import { ModalContract } from "./modals/ModalContract";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";

const Sidebar = () => {
  const { user, userPermission, refetchUser } = useApplication();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [titlePage, setTitlePage] = useState("Dashboard");
  const sidebarRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [isOpenModalContract, setIsOpenModalContract] = useState(false);
  const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const hoverCloseTimeoutRef = useRef(null);
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 640 : false
  );
  const COLLAPSED_WIDTH = 75;
  const EXPANDED_WIDTH = 256;
  const isMobileView = !isDesktopView;
  const sidebarWidth = isMobileView
    ? EXPANDED_WIDTH
    : isCollapsed
    ? COLLAPSED_WIDTH
    : EXPANDED_WIDTH;
  const contentMarginLeft = isDesktopView ? sidebarWidth : 0;
  const contentWidth = isDesktopView
    ? `calc(100% - ${sidebarWidth}px)`
    : "100%";
  const isObahCredito =
    typeof window !== "undefined" &&
    window.location.href.includes("obahcredito");
  const isRepresentative = user?.Company?.companyTypeId === "REPRESENTANTE";
  const shouldHideObahMenuItems = isObahCredito && !isRepresentative;
  const obahHiddenMenuItems = new Set([
    "Minhas Faturas",
    "Usuários",
    "Documentação",
    "Suporte",
  ]);
  const clearHoverClose = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    clearHoverClose();
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setHoverRect(null);
    }, 180);
  }, [clearHoverClose]);

  const collapsedTooltip = useMemo(() => {
    if (
      !isDesktopView ||
      !isCollapsed ||
      !hoveredMenu ||
      !hoverRect ||
      typeof document === "undefined"
    ) {
      return null;
    }

    const allowedChildren = (hoveredMenu.children || []).filter((child) =>
      permissionChecker(
        child.permission,
        userPermission,
        user,
        child.typeCompany ?? hoveredMenu.typeCompany
      )
    );

    if (!allowedChildren.length) return null;

    const style = {
      position: "fixed",
      top: hoverRect.top,
      left: hoverRect.right + 8,
      width: 220,
      zIndex: 9999,
    };

    return createPortal(
      <div
        className="bg-white/95 text-slate-800 rounded-lg shadow-lg border border-slate-200 pointer-events-auto"
        style={style}
        onMouseEnter={clearHoverClose}
        onMouseLeave={scheduleHoverClose}
      >
        <div className="flex flex-col p-2 gap-1">
          {allowedChildren.map((child, idx) => {
            const isChildActive = location.pathname === child.link;
            return (
              <Link
                to={child.link}
                key={idx}
                onClick={() => {
                  handleToggleSidebar(formatTitle(child.name));
                  setHoveredMenu(null);
                }}
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                  isChildActive
                    ? "text-green-600 font-semibold"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                {child.icon && <Icon className="text-base">{child.icon}</Icon>}
                <span className="truncate">{child.name}</span>
              </Link>
            );
          })}
        </div>
      </div>,
      document.body
    );
  }, [
    isCollapsed,
    hoveredMenu,
    hoverRect,
    userPermission,
    user,
    location.pathname,
    clearHoverClose,
    scheduleHoverClose,
    isDesktopView,
  ]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const widthValue = isDesktopView ? `${sidebarWidth}px` : "0px";
    document.documentElement.style.setProperty("--sidebar-width", widthValue);
  }, [sidebarWidth, isDesktopView]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        isCollapsed ? "true" : "false"
      );
    } catch {
      // ignore write errors
    }
  }, [isCollapsed]);
  const daysLeft = calculateExpirationPeriodTest(
    user?.Company?.planExpirationDate
  );
  const canSeeInvoices = permissionChecker(["ADMIN"], userPermission, user, [
    "ALL",
  ]);
  const isAdminUser = Array.isArray(userPermission)
    ? userPermission.includes("ADMIN")
    : userPermission === "ADMIN";
  const canSeeContract = isAdminUser;
  const coercePaid = (isPaid) =>
    isPaid === true || isPaid === 1 || isPaid === "1";

  const { data: hasBoletoNotification = false } = useQuery(
    ["sidebar-boleto-notification", user?.Company?.id],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?isPaid=false&itemsPerPage=-1`
      );
      const rows = data?.data?.data || [];
      return rows.some(
        (inv) => inv?.type === "BOLETO" && !coercePaid(inv?.isPaid)
      );
    },
    {
      enabled: Boolean(canSeeInvoices && user?.Company?.id),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const handleToggleSidebar = (linkName = "") => {
    setIsOpen(!isOpen);
    if (linkName !== "") setTitlePage(linkName);
  };

  const toggleCollapse = () => {
    if (!isDesktopView) return;
    setIsCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (!isDesktopView && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isDesktopView, isCollapsed]);

  const toggleMenu = (menuName) => {
    setExpandedMenus((prev) => {
      const isAlreadyExpanded = prev[menuName];
      if (isAlreadyExpanded) return {};
      return { [menuName]: true };
    });
  };

  const handleRefresh = () => {
    refetchUser();
  };

  /* ===== helper de título ===== */
  const formatTitle = (name = "") => {
    const n = (name || "").toLowerCase();
    if (n.includes("crédito") || n === "créditos")
      return "Consultas de crédito";
    if (n.includes("cadastra") || n === "cadastrais")
      return "Consultas cadastrais";
    if (n.includes("veicular") || n === "veiculares")
      return "Consultas veiculares";
    return name || "Visão Geral";
  };

  const getTitleFromPath = (pathname) => {
    if (pathname.startsWith("/queries/credits")) return "Consultas de crédito";
    if (pathname.startsWith("/queries/cadastrals"))
      return "Consultas cadastrais";
    if (pathname.startsWith("/queries/autos")) return "Consultas veiculares";
    return (
      menuLinks.find((link) => link.link === pathname)?.name || "Visão Geral"
    );
  };

  /* ===== PERFIL ===== */
  const isSeller = Array.isArray(userPermission)
    ? userPermission.includes("SELLER")
    : userPermission === "SELLER";

  /* ===== MENU BASE (não SELLER) ===== */
  const baseMenuLinks = [
    {
      name: "Visão Geral",
      link: "/dashboard",
      icon: "equalizer",
      permission: ["USER", "USER_CALENDARS"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Consulte aqui",
      link: "/queries",
      icon: "manage_search",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Motor de crédito",
          link: "/credit-engine",
          icon: "verified_user",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Créditos",
          link: "/queries/credits",
          icon: "attach_money",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Cadastrais",
          link: "/queries/cadastrals",
          icon: "account_box",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Veiculares",
          link: "/queries/autos",
          icon: "time_to_leave",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },
    {
      name: "Localizador PF/PJ",
      link: "/localize",
      icon: "travel_explore",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Negative Clientes",
      link: "/negatives",
      icon: "thumb_down_alt",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Relatórios",
      link: "/reports",
      icon: "query_stats",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Consultas",
          link: "/reports/queries",
          icon: "manage_search",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },

    {
      name: "Documentação",
      link: "/docs",
      icon: "article",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Minhas Faturas",
      link: "/invoices",
      icon: "request_quote",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Usuários",
      link: "/users",
      icon: "group",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Dúvidas Frequentes",
      link: "/faq",
      icon: "help_outline",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Suporte",
      link: "/support",
      icon: "support_agent",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Administrativo",
      link: "/support",
      icon: "settings",
      permission: ["ADMIN"],
      isCollapsible: true,
      typeCompany: ["REPRESENTANTE", "ADMIN"],
      children: [
        {
          name: "Review ⭐",
          link: "/admin/daily-review",
          icon: "stacked_bar_chart",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Relatórios",
          link: "/admin/reports",
          icon: "query_stats",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Financeiro",
          link: "/admin/invoices",
          icon: "receipt",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Clientes",
          link: "/admin/customers",
          icon: "person",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Representantes",
          link: "/admin/representatives",
          icon: "group_2",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Tabela de Custos",
          link: "/admin/table-cost",
          icon: "request_page",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Banners",
          link: "/admin/banners",
          icon: "image",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Configurações",
          link: "/admin/admin",
          icon: "settings_suggest",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
      ],
    },
  ];

  /* ===== MENU SELLER (sem o "Administrativo") ===== */
  const rawSellerMenu = [
    {
      name: "Dashboard",
      link: "/dashboard",
      icon: "equalizer",
      permission: ["USER", "USER_CALENDARS"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Consultas",
      link: "/queries",
      icon: "manage_search",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Créditos",
          link: "/queries/credits",
          icon: "attach_money",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Cadastrais",
          link: "/queries/cadastrals",
          icon: "account_box",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Veiculares",
          link: "/queries/autos",
          icon: "time_to_leave",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },
    {
      name: "Localizador PF/PJ",
      link: "/localize",
      icon: "travel_explore",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Negative Clientes",
      link: "/negatives",
      icon: "thumb_down_alt",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Relatórios",
      link: "/reports",
      icon: "query_stats",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Consultas",
          link: "/reports/queries",
          icon: "manage_search",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },

    // ✅ novo item só para SELLER
    {
      name: "Meus Clientes",
      link: "/my-customers",
      icon: "groups_3",
      permission: ["SELLER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },

    {
      name: "Dúvidas Frequentes",
      link: "/faq",
      icon: "help_outline",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Suporte",
      link: "/support",
      icon: "support_agent",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
  ];

  /* === Dá permissão SELLER para todos os itens/filhos do menu SELLER === */
  const addSellerPermission = (item) => {
    const patched = {
      ...item,
      permission: Array.from(new Set([...(item.permission || []), "SELLER"])),
      typeCompany:
        item.typeCompany && item.typeCompany.length
          ? item.typeCompany
          : ["ALL"],
    };
    if (item.children && item.children.length) {
      patched.children = item.children.map(addSellerPermission);
    }
    return patched;
  };

  const sellerMenuLinks = useMemo(
    () => rawSellerMenu.map(addSellerPermission),
    []
  );

  /* ===== Escolha final conforme perfil ===== */
  const menuLinks = isSeller ? sellerMenuLinks : baseMenuLinks;

  const closeSidebar = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsDesktopView(window.innerWidth >= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !menuBtnRef.current.contains(event.target) &&
        isOpen
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [location.pathname, isOpen]);

  useEffect(() => {
    setTitlePage(formatTitle(getTitleFromPath(location.pathname)));
  }, [location.pathname, menuLinks]);

  let logoSrc;

  switch (true) {
    case window.location.href.includes("avaliecreditoconsultas"):
      logoSrc = "/assets/white-label/avalie-credito/logo_branco.png";
      break;
    case window.location.href.includes("acessopositivo"):
      logoSrc = "/assets/white-label/acesso-positivo/logo_acesso.png";
      break;
    case window.location.href.includes("proconsultbr"):
      logoSrc = "/assets/white-label/pro-consult-br/logo_branco.png";
      break;
    case window.location.href.includes("simscore"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_branco.png";
      break;
    case window.location.href.includes("simpesquisa"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_branco.png";
      break;
    case window.location.href.includes("credoresbrasil"):
      logoSrc = "/assets/white-label/credores-brasil/logo_branco.png";
      break;
    case window.location.href.includes("consultaspositivo"):
      logoSrc = "/assets/white-label/consultas-positivo/logo_branco.png";
      break;
    case window.location.href.includes("raioxconsultas"):
      logoSrc = "/assets/white-label/raio-x/logo_branco.png";
      break;
    case window.location.href.includes("obahcredito"):
      logoSrc = "/assets/white-label/obah-credito/logo_branco.png";
      break;
    case window.location.href.includes("hellusconsultoria"):
      logoSrc = "/assets/white-label/hellus-consultoria/logo_branco.png";
      break;
    case window.location.href.includes("nomenoazul"):
      logoSrc = "/assets/white-label/nome-no-azul/logo_branco.png";
      break;
    case window.location.href.includes("finposcore"):
      logoSrc = "/assets/white-label/finpo-score/logo_branco.png";
      break;
    case window.location.href.includes("locagora"):
      logoSrc = "/assets/white-label/locagora/logo_branco.png";
      break;
    case window.location.href.includes("riscocred"):
      logoSrc = "/assets/white-label/riscocred/logo_branco.png";
      break;
    default:
      logoSrc = "/assets/logo-branco.png";
      break;
  }

  return (
    <>
      <aside
        id="logo-sidebar"
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-40 h-screen transition-transform bg-white border-r sm:translate-x-0 ${
          isOpen ? "" : "-translate-x-full"
        } dark:bg-gray-800 dark:border-gray-700`}
        aria-label="Sidebar"
        style={{
          width: `${sidebarWidth}px`,
          transition: "width 0.25s ease",
        }}
      >
        <div className="h-full px-3 pb-4 overflow-y-auto bg-white dark:bg-gray-800 pt-3 no-scrollbar">
          <img
            src={logoSrc}
            alt="Logo da RelinqScore"
            style={{
              height: isCollapsed ? "20px" : "48px",
              marginBottom: isCollapsed ? "20px" : "",
              marginTop: isCollapsed ? "10px" : "",
            }}
            className="mx-auto mb-4 logo-carro"
          />
          <div className="flex justify-center mb-4 hidden sm:flex">
            <button
              onClick={toggleCollapse}
              type="button"
              className="inline-flex items-center px-3 py-1 text-xs font-semibold tracking-wide text-gray-600 bg-gray-100 rounded-full shadow-sm transition hover:bg-gray-200 focus:outline-none"
            >
              {isCollapsed ? (
                <IoIosArrowForward className="text-base" />
              ) : (
                <>
                  <IoIosArrowBack className="text-base" />
                  <span className="mr-2">Esconder menu</span>
                </>
              )}
            </button>
          </div>

          {user?.Company?.typePayment === "PRE" && (
            <div className="flex gap-2 justify-center items-center mb-4">
              <Badge className="w-fit shadow text-sm">
                <div>Saldo: {formatToReal(user?.Company?.credit || 0)}</div>
              </Badge>
              <button
                data-tooltip-id="tooltip-add-credit"
                className="rounded shadow bg-primary text-white px-2 py-1"
                onClick={() => setIsOpenModalAddCredit(true)}
              >
                <MdOutlinePayments fontSize={18} />
              </button>
              <Tooltip id="tooltip-add-credit" content="Adicionar crédito" />
              <button
                data-tooltip-id="tooltip-refresh-credit"
                className="rounded shadow bg-white text-white px-2 py-1"
                onClick={handleRefresh}
              >
                <IoMdRefresh fontSize={18} className="color-primary" />
              </button>
              <Tooltip id="tooltip-refresh-credit" content="Atualizar saldo" />
            </div>
          )}

          {daysLeft > 0 && (
            <nav className="flex flex-col gap-2 mt-6 px-1 text-sm text-white/90">
              {menuLinks.map((menuLink, index) => {
                const isActive = location.pathname === menuLink.link;
                const isExpanded = expandedMenus[menuLink.name];
                const hasChildren =
                  Array.isArray(menuLink.children) &&
                  menuLink.children.length > 0;
                const hasPermission = permissionChecker(
                  menuLink.permission,
                  userPermission,
                  user,
                  menuLink.typeCompany
                );
                const showBoletoNotification =
                  menuLink.name === "Minhas Faturas" && hasBoletoNotification;

                if (
                  shouldHideObahMenuItems &&
                  obahHiddenMenuItems.has(menuLink.name)
                ) {
                  return null;
                }

                if (!hasPermission) return null;

                return (
                  <div key={index}>
                    {!menuLink.isCollapsible ? (
                      <Link
                        to={menuLink.link}
                        onClick={() =>
                          handleToggleSidebar(formatTitle(menuLink.name))
                        }
                        title={isCollapsed ? menuLink.name : undefined}
                        className={`flex items-center px-3 py-2 rounded-lg transition-all duration-150 group ${
                          isCollapsed ? "justify-center gap-0" : "gap-3"
                        } ${
                          isActive
                            ? "bg-white/10 text-white border-l-4 border-green-400"
                            : "hover:bg-white/5"
                        } ${showBoletoNotification ? "relative" : ""}`}
                      >
                        <Icon
                          className={`text-lg ${
                            isActive
                              ? "text-green-400"
                              : "text-white/60 group-hover:text-white"
                          }`}
                        >
                          {menuLink.icon}
                        </Icon>
                        {!isCollapsed && (
                          <span className="font-medium">{menuLink.name}</span>
                        )}
                        {showBoletoNotification && (
                          <span
                            className={`inline-flex relative ${
                              isCollapsed ? "absolute top-2 right-2" : "ml-auto"
                            }`}
                          >
                            <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 opacity-75 animate-ping" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 shadow" />
                          </span>
                        )}
                      </Link>
                    ) : (
                      <>
                        <div
                          className={`relative ${
                            isCollapsed ? "flex justify-center" : ""
                          }`}
                          onMouseEnter={(event) => {
                            if (!isCollapsed) return;
                            clearHoverClose();
                            setHoveredMenu(menuLink);
                            const rect =
                              event.currentTarget.getBoundingClientRect();
                            setHoverRect(rect);
                          }}
                          onMouseMove={(event) => {
                            if (!isCollapsed) return;
                            const rect =
                              event.currentTarget.getBoundingClientRect();
                            setHoverRect(rect);
                          }}
                          onMouseLeave={() => {
                            if (!isCollapsed) return;
                            scheduleHoverClose();
                          }}
                        >
                          <button
                            onClick={() => toggleMenu(menuLink.name)}
                            title={isCollapsed ? menuLink.name : undefined}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition group ${
                              isCollapsed ? "justify-center" : "justify-between"
                            } hover:bg-white/5`}
                          >
                            <div
                              className={`flex items-center ${
                                isCollapsed ? "justify-center" : "gap-3"
                              }`}
                            >
                              <Icon className="text-lg text-white/60 group-hover:text-white">
                                {menuLink.icon}
                              </Icon>
                              {!isCollapsed && (
                                <span className="font-medium">
                                  {menuLink.name}
                                </span>
                              )}
                            </div>
                            {!isCollapsed && (
                              <IoIosArrowDown
                                className={`transition-transform text-white/50 group-hover:text-white ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                              />
                            )}
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && !isCollapsed && (
                              <motion.div
                                key="submenu"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.25,
                                  ease: "easeInOut",
                                }}
                                className="overflow-hidden mt-2 px-2"
                              >
                                <div className="bg-white/10 rounded-lg px-2 py-1 flex flex-col gap-1.5">
                                  {(menuLink.children || [])
                                    .filter((child) =>
                                      permissionChecker(
                                        child.permission,
                                        userPermission,
                                        user,
                                        child.typeCompany ??
                                          menuLink.typeCompany
                                      )
                                    )
                                    .map((child, subIndex) => (
                                      <Link
                                        to={child.link}
                                        key={subIndex}
                                        onClick={() =>
                                          handleToggleSidebar(
                                            formatTitle(child.name)
                                          )
                                        }
                                        className={`group flex items-center gap-2 px-2 py-1 rounded-md text-[13px] transition-all duration-150 ${
                                          location.pathname === child.link
                                            ? "text-white font-medium bg-white/10"
                                            : "text-white/50 hover:text-white"
                                        }`}
                                      >
                                        {child.icon && (
                                          <Icon
                                            className={`text-[16px] ${
                                              location.pathname === child.link
                                                ? "text-green-400"
                                                : "text-white/30 group-hover:text-white/60"
                                            }`}
                                          >
                                            {child.icon}
                                          </Icon>
                                        )}
                                        <span className="truncate">
                                          {child.name}
                                        </span>
                                      </Link>
                                    ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </nav>
          )}
        </div>
      </aside>

      <div
        className="mt-0 transition-all duration-300"
        style={{
          marginLeft: `${contentMarginLeft}px`,
          width: contentWidth,
        }}
      >
        <div className="container mx-auto">
          <div className="mainbar px-5">
            <div className="flex justify-between items-center">
              <div className="flex gap-2 items-center">
                <h2 className="font-bold">{titlePage}</h2>
              </div>
              <div className="flex items-center gap-3">
                <h2 className="hidden md:block">Bem vindo, {user?.name}</h2>
                <Dropdown
                  label={<Avatar rounded size="sm" img={user?.imageUrl} />}
                  arrowIcon={false}
                  inline
                  className="z-50"
                >
                  <Dropdown.Header>
                    <span className="block truncate text-sm font-medium">
                      {user?.email}
                    </span>
                  </Dropdown.Header>
                  <Dropdown.Item className="py-0 px-0">
                    <Link
                      to="/profile"
                      className="w-full py-2 text-left px-4"
                      onClick={() => setTitlePage("Meu Perfil")}
                    >
                      Meu perfil
                    </Link>
                  </Dropdown.Item>
                  {canSeeContract && (
                    <Dropdown.Item className="py-0 px-0">
                      <button
                        type="button"
                        className="w-full py-2 text-left px-4"
                        onClick={() => setIsOpenModalContract(true)}
                      >
                        Meu contrato
                      </button>
                    </Dropdown.Item>
                  )}

                  <Dropdown.Divider />
                  <Dropdown.Item className="py-0 px-0">
                    <Link
                      to="/lgpd"
                      className="w-full py-2 text-left px-4"
                      onClick={() => setTitlePage("Aviso LGPD")}
                    >
                      Aviso LGDP
                    </Link>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={removeToken}>Sair</Dropdown.Item>
                </Dropdown>
                <button
                  ref={menuBtnRef}
                  type="button"
                  onClick={() => handleToggleSidebar()}
                  className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden focus:outline-none dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 md:hidden"
                >
                  <span className="sr-only">Abrir sidebar</span>
                  <svg
                    className="w-6 h-6"
                    aria-hidden="true"
                    fill="#fff"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      clipRule="evenodd"
                      fillRule="evenodd"
                      d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />
      <ModalContract
        isOpen={isOpenModalContract}
        onClose={() => setIsOpenModalContract(false)}
      />
      {collapsedTooltip}
    </>
  );
};

export default Sidebar;
